<template>
  <div
    class="p-4 digital-incorporation-form"
    style="min-height: 100svh; display: flex; flex-direction: column"
  >
    <div class="mb-3">
      <a-skeleton
        active
        :loading="logo_loading"
        v-if="current_component !== 'SignedDocumentSuccessful'"
      >
        <img
          :src="logo_src"
          alt="Logo principal"
          style="max-width: 300px; max-height: 100px"
        />
      </a-skeleton>
    </div>

    <div v-if="current_step < 7">
      <a-typography-title class="ant-page-header-heading-title" :level="4">
        Formulario de solicitud incorporación digital
      </a-typography-title>

      <a-steps
        progress-dot
        :current="current_visible_step"
        :direction="stepDirection"
        class="my-5 digital-form-steps"
      >
        <a-step
          v-for="visibleStep in visible_steps"
          :key="visibleStep.step"
          :title="visibleStep.title"
          @click="() => redirectionToStep(visibleStep.step)"
          :class="{ 'pointer-step': visibleStep.step <= on_load_step }"
        />
      </a-steps>
    </div>

    <a-skeleton active :loading="loading_component">
      <ContractingAndPolicies
        ref="contractingAndPoliciesRef"
        v-if="current_component === 'ContractingAndPolicies'"
      />
      <OwnerStep ref="ownerStepRef" v-if="current_component === 'OwnerStep'" />
      <BackgroundLoad
        ref="backgroundLoadRef"
        v-if="current_component === 'BackgroundLoad'"
      />
      <DependentCoverageAssignment
        ref="dependentCoverageAssignmentRef"
        v-if="current_component === 'DependentCoverageAssignment'"
      />
      <Recipients
        ref="recipientsRef"
        v-if="current_component === 'Recipients'"
      />
      <HealthDeclaration
        ref="healthDeclarationRef"
        v-if="current_component === 'HealthDeclaration'"
      />
      <Consent ref="consentRef" v-if="current_component === 'Consent'" />
      <DocumentPrevisualization
        v-if="current_component === 'DocumentPrevisualization'"
      />
      <SignedDocumentSuccessful
        v-if="current_component === 'SignedDocumentSuccessful'"
      />
    </a-skeleton>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, computed, watchEffect, ref } from 'vue';
import ContractingAndPolicies from '@/components/digital-incorporation-form/contracting-party-and-policies-step';
import OwnerStep from '@/components/digital-incorporation-form/owner-data-step';
import BackgroundLoad from '@/components/digital-incorporation-form/background-load';
import DependentCoverageAssignment from '@/components/digital-incorporation-form/dependent-coverage-assignment';
import Recipients from '@/components/digital-incorporation-form/beneficiaries';
import HealthDeclaration from '@/components/digital-incorporation-form/health-declaration';
import Consent from '@/components/digital-incorporation-form/consent';
import DocumentPrevisualization from '@/components/digital-incorporation-form/document-previsualization';
import SignedDocumentSuccessful from '@/components/digital-incorporation-form/signed-document-successful.vue';
import scrollToTopScreen from '@/utils/scrollToTopScreen';

import { useStore } from 'vuex';
import {
  SAVE_DIGITAL_FORM_TOKEN,
  GET_DIGITAL_FORM_STEP,
  GET_DIGITAL_FORM_HAS_DEPENDENTS,
  FETCH_DIGITAL_FORM,
  FETCH_DEPENDANTS,
  ACTION_CHANGE_STEP,
  FETCH_INCORPORATION_LOGO
} from '@/store/types';
import { useRoute, useRouter } from 'vue-router';
import { getVisibleDigitalFormSteps } from '@/utils/digitalFormSteps';

const contractingAndPoliciesRef = ref();
const ownerStepRef = ref();
const backgroundLoadRef = ref();
const dependentCoverageAssignmentRef = ref();
const recipientsRef = ref();
const healthDeclarationRef = ref();
const consentRef = ref();

const store = useStore();
const route = useRoute();
const router = useRouter();
const current_step = computed(() => store.getters[GET_DIGITAL_FORM_STEP] - 1);
// The stepper hides "Asignación de coberturas" when the form has no
// dependents; step numbers themselves stay aligned with the backend.
const visible_steps = computed(() =>
  getVisibleDigitalFormSteps(store.getters[GET_DIGITAL_FORM_HAS_DEPENDENTS])
);
const current_visible_step = computed(() =>
  visible_steps.value.findIndex(
    ({ step }) => step === store.getters[GET_DIGITAL_FORM_STEP]
  )
);
const isNarrowScreen = ref(false);
const stepDirection = computed(() =>
  isNarrowScreen.value ? 'vertical' : 'horizontal'
);
const on_load_step = ref();
const current_component = ref();
const loading_component = ref(false);
const logo_loading = ref(false);
const logo_src = ref();

watchEffect(() => {
  // Cambio el componente dependiendo de el step que cambie
  const new_step = current_step.value;

  const steps = {
    0: 'ContractingAndPolicies',
    1: 'OwnerStep',
    2: 'BackgroundLoad',
    3: 'DependentCoverageAssignment',
    4: 'Recipients',
    5: 'HealthDeclaration',
    6: 'Consent',
    7: 'DocumentPrevisualization',
    8: 'SignedDocumentSuccessful'
  };

  current_component.value = steps[new_step];
  scrollToTopScreen();

  // Guardo el valor maximo de el step en el que estube
  // para prevenir que hagan click en un step que no han pasado antes
  if (current_step.value + 1 > on_load_step.value) {
    on_load_step.value = current_step.value + 1;
  }
});

const getAndSaveToken = () => {
  return new Promise((resolve, reject) => {
    const token = route.params.token;
    store
      .dispatch(SAVE_DIGITAL_FORM_TOKEN, token)
      .then((resp) => {
        resolve(resp);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getCurrentStep = () => {
  return new Promise((resolve, reject) => {
    store
      .dispatch(FETCH_DIGITAL_FORM)
      .then((resp) => {
        const { data } = resp;
        // The ticket was cancelled or already sent to the insurer.
        if (data.isTicketAvailable === false) {
          router.push({ name: 'DigitalFormUnavailable' });
          reject(new Error('Digital form not available'));
          return;
        }
        on_load_step.value = data.currentStep;
        resolve(resp);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const getLogo = () => {
  return new Promise((resolve, reject) => {
    logo_loading.value = true;
    store
      .dispatch(FETCH_INCORPORATION_LOGO)
      .then((resp) => {
        const { logoObjectUrl } = resp;
        logo_src.value = logoObjectUrl;
        resolve(resp);
      })
      .catch((error) => {
        reject(error);
      })
      .finally(() => {
        logo_loading.value = false;
      });
  });
};

const updateStepDirection = () => {
  isNarrowScreen.value = window.innerWidth <= 1024;
};

const redirectionToStep = (step) => {
  if (step <= on_load_step.value) {
    switch (current_component.value) {
      case 'ContractingAndPolicies':
        contractingAndPoliciesRef.value.savePolicies(step);
        break;
      case 'OwnerStep':
        ownerStepRef.value.formSubmit(step);
        break;
      case 'BackgroundLoad':
        backgroundLoadRef.value.formSubmit(step);
        break;
      case 'DependentCoverageAssignment':
        dependentCoverageAssignmentRef.value.formSubmit(step);
        break;
      case 'Recipients':
        recipientsRef.value.formSubmit(step);
        break;
      case 'HealthDeclaration':
        healthDeclarationRef.value.formValidation(step);
        break;
      case 'Consent':
        consentRef.value.formSubmit(step);
        break;
      default:
        break;
    }
  }
};

onMounted(() => {
  updateStepDirection();
  window.addEventListener('resize', updateStepDirection);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStepDirection);
});

// Loads whether the form has dependents and moves a resumed form out of the
// dependent coverage step when there are none. On failure the step is kept.
const loadDependentsAndNormalizeStep = async () => {
  try {
    await store.dispatch(FETCH_DEPENDANTS);
    await store.dispatch(
      ACTION_CHANGE_STEP,
      store.getters[GET_DIGITAL_FORM_STEP]
    );
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  try {
    await getAndSaveToken();
    await getCurrentStep();
    await loadDependentsAndNormalizeStep();
    await getLogo();
  } catch (error) {
    console.error(error?.response?.data?.message || error);
  }
});
</script>

<style scoped>
.digital-incorporation-form {
  width: 100%;
  max-width: 1448px;
  margin: 0 auto;
  box-sizing: border-box;
}

.pointer-step {
  cursor: pointer;
}

.digital-form-steps :deep(.ant-steps-item-process .ant-steps-item-icon),
.digital-form-steps :deep(.ant-steps-item-finish .ant-steps-item-icon) {
  background: transparent !important;
  border: 0 !important;
}

.digital-form-steps :deep(.ant-steps-item-process .ant-steps-icon-dot),
.digital-form-steps :deep(.ant-steps-item-finish .ant-steps-icon-dot) {
  background-color: #2521e5 !important;
}

@media (max-width: 1024px) {
  .digital-form-steps {
    margin-bottom: 1.5rem !important;
  }
}
</style>
